import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  MetadataProposal, MetadataProposalResult, SpotifyCandidateSummary,
  METADATA_PROPOSAL_REVIEWABLE_FIELDS
} from '../../ressources/types/metadataProposal';

const FIELD_LABELS: { [key: string]: string } = {
  title: 'Titre',
  artist: 'Artiste',
  album: 'Album',
  releaseDate: 'Date de sortie',
  label: 'Label',
  cover: 'Pochette',
  genre: 'Genre',
  nationality: 'Nationalité',
  artistImage: 'Photo artiste'
};

const IMAGE_FIELDS = ['cover', 'artistImage'];

interface ReviewRow {
  field: keyof MetadataProposal;
  label: string;
  isImage: boolean;
  currentDisplay: string;
  proposedDisplay: string;
  // true = rien retrouvé cette fois pour ce champ, alors qu'une valeur actuelle existe : cocher revient
  // à la vider (une éventuelle mauvaise association passée ne reste pas invisible dans la review).
  unconfirmed: boolean;
  checked: boolean;
}

@Component({
  selector: 'app-metadata-review',
  templateUrl: './metadataReview.component.html',
  styleUrls: ['./metadataReview.component.scss']
})
export class MetadataReviewComponent implements OnInit {

  rows: ReviewRow[] = [];
  spotifyCandidates: SpotifyCandidateSummary[] = [];
  selectedCandidateIndex = 0;
  loading = true;
  errorMessage: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dataIn: {
      resultPromise: Promise<MetadataProposalResult>;
      recomputeForCandidate: (candidateIndex: number) => MetadataProposalResult;
    },
    private dialogRef: MatDialogRef<any>) {}

  ngOnInit() {
    // La modale s'ouvre tout de suite (spinner) plutôt que d'attendre la fin de la recherche
    // (Deezer/Spotify/MusicBrainz peuvent prendre plusieurs secondes) pour donner un retour immédiat.
    this.dataIn.resultPromise
      .then(result => {
        this.applyResult(result);
        this.loading = false;
      })
      .catch(() => {
        this.errorMessage = 'La recherche de métadonnées a échoué.';
        this.loading = false;
      });
  }

  private applyResult(result: MetadataProposalResult) {
    this.spotifyCandidates = result.spotifyCandidates || [];
    this.selectedCandidateIndex = result.spotifyCandidateIndex || 0;
    this.rows = this.buildRows(result.proposal);
  }

  private buildRows(proposal: MetadataProposal): ReviewRow[] {
    return METADATA_PROPOSAL_REVIEWABLE_FIELDS
      .filter(field => {
        const entry = proposal[field];
        // Changé (nouvelle valeur) OU rien retrouvé mais une valeur actuelle existe (à faire confirmer/vider).
        return !!entry && (entry.changed || (entry.proposed === undefined && entry.current !== undefined));
      })
      .map(field => {
        const entry = proposal[field]!;
        const unconfirmed = !entry.changed && entry.proposed === undefined;
        return {
          field,
          label: FIELD_LABELS[field] || field,
          isImage: IMAGE_FIELDS.includes(field),
          currentDisplay: this.formatValue(field, entry.current),
          proposedDisplay: unconfirmed ? 'Rien retrouvé' : this.formatValue(field, entry.proposed),
          unconfirmed,
          // Une vraie proposition est cochée par défaut (on l'applique) ; un champ non reconfirmé ne
          // l'est pas (vider est une action volontaire, pas la valeur par défaut).
          checked: entry.changed
        };
      });
  }

  formatValue(field: keyof MetadataProposal, value: any): string {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    switch (field) {
      case 'releaseDate':
        return value.release_date ? `${value.release_date} (${value.year || ''})` : '—';
      case 'genre':
        return [value.genre1, value.genre2, value.genre3].filter(v => !!v).join(' / ') || '—';
      case 'nationality':
        return value.countryName ? `${value.countryName} (${value.vef || ''})` : '—';
      default:
        return typeof value === 'string' ? value : '—';
    }
  }

  // Recalcul pur côté table.component.ts (pas de nouvel appel réseau) : Titre/Artiste/Album/Date de
  // sortie/Label/Pochette repartent du candidat choisi, le reste (genre, nationalité...) est inchangé.
  selectCandidate(index: number) {
    if (index === this.selectedCandidateIndex) {
      return;
    }
    const result = this.dataIn.recomputeForCandidate(index);
    this.applyResult(result);
  }

  checkAll(checked: boolean) {
    for (const row of this.rows) {
      row.checked = checked;
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  apply(): void {
    const fieldsToApply = this.rows.filter(row => row.checked).map(row => row.field);
    this.dialogRef.close(fieldsToApply);
  }

}
